import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useId, useState } from 'react';
import { strength } from '@digipost/dds-password-security';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLanguage } from '@digipost/translate';

var inputContainer = "Input-module_inputContainer__SMazU";
var warn$1 = "Input-module_warn__XyjJC";
var warnText$1 = "Input-module_warnText__lsHD8";

const Input = forwardRef(({ inputType = "text", inputClass = "", extraWrapperClass = "", labelText = null, labelClass, descriptionText = null, descriptionClass, errorText = null, errorClass = warnText$1, errorIcon = null, focus = false, valid = true, id, children, ...otherProps }, ref) => {
    const uniqueId = useId();
    const inputId = id || `input-${uniqueId}`;
    return (jsxs("div", { className: `${inputContainer} ${extraWrapperClass}`, children: [labelText !== null && (jsx("label", { className: labelClass, htmlFor: inputId, children: labelText })), descriptionText !== null && (jsx("div", { className: descriptionClass, id: `${inputId}-description`, children: descriptionText })), jsx("input", { ref: ref, "aria-describedby": `${inputId}-description`, "aria-errormessage": errorText !== null ? `${inputId}-error` : undefined, "aria-invalid": !valid, className: `${inputClass} ${valid ? "" : warn$1}`, type: inputType, id: inputId, autoFocus: focus, ...otherProps }), errorText !== null && (jsxs("div", { className: errorClass, role: "status", id: `${inputId}-error`, children: [errorIcon, errorText] })), children] }));
});
Input.displayName = "Input";

var meter = "PasswordInput-module_meter__3uKhy";
var line = "PasswordInput-module_line__9PKmk";
var empty = "PasswordInput-module_empty__etHav";
var status = "PasswordInput-module_status__c12gP";
var s0 = "PasswordInput-module_s0__lGjLZ";
var s1 = "PasswordInput-module_s1__1wPtW";
var s2 = "PasswordInput-module_s2__MKyw2";
var s3 = "PasswordInput-module_s3__NVesP";
var PasswordInput_module = {"meter":"PasswordInput-module_meter__3uKhy","line":"PasswordInput-module_line__9PKmk","empty":"PasswordInput-module_empty__etHav","status":"PasswordInput-module_status__c12gP","s0":"PasswordInput-module_s0__lGjLZ","s1":"PasswordInput-module_s1__1wPtW","s2":"PasswordInput-module_s2__MKyw2","s3":"PasswordInput-module_s3__NVesP"};

var styles = /*#__PURE__*/Object.freeze({
  __proto__: null,
  default: PasswordInput_module,
  empty: empty,
  line: line,
  meter: meter,
  s0: s0,
  s1: s1,
  s2: s2,
  s3: s3,
  status: status
});

const PasswordInput = forwardRef(({ strengthText, onChange, errorText, ...otherProps }, ref) => {
    const [password, setPassword] = useState("");
    const [strength$1, setStrength] = useState();
    const handleInputChange = (event) => {
        const value = event.target.value;
        const newStrength = strength(value);
        setPassword(value);
        setStrength(value ? strength(value) : undefined);
        if (onChange) {
            onChange(value, newStrength);
        }
    };
    const icons = [
        () => jsx(FontAwesomeIcon, { icon: faTimes }),
        () => jsx(FontAwesomeIcon, { icon: faCheck }),
        () => jsx(FontAwesomeIcon, { icon: faCheck }),
        () => jsx(FontAwesomeIcon, { icon: faStar }),
    ];
    const width = `${Math.min(strength$1?.ok ? 100 : 50, 1.5 * Math.log(strength$1?.entropy ? strength$1.entropy.space : 0))}%`;
    const getErrorText = () => {
        if (errorText) {
            return errorText;
        }
        else if (strength$1) {
            return strengthText(strength$1);
        }
    };
    return (jsx(Input, { ref: ref, inputType: "password", onChange: handleInputChange, extraWrapperClass: `${meter}`, errorIcon: icons[strength$1?.strength || 0](), errorText: getErrorText(), ...otherProps, children: jsx("div", { className: `${line} ${password.length > 0 ? "" : empty}`, role: "presentation", children: jsx("div", { className: `${status} ${styles[`s${strength$1?.strength || 0}`]}`, style: { width } }) }) }));
});
PasswordInput.displayName = "PasswordInput";

var textareaContainer = "TextArea-module_textareaContainer__9zks6";
var warn = "TextArea-module_warn__hS0Om";
var maxLength = "TextArea-module_maxLength__yqsCG";
var warnText = "TextArea-module_warnText__5580O";

function getDefaultExceededText(language) {
    switch (language) {
        case "nb":
            return "tegn for mye";
        case "nn":
            return "teikn for mykje";
        case "en":
            return "characters too many";
    }
}
function getDefaultRemainingText(language) {
    switch (language) {
        case "nb":
            return "tegn igjen";
        case "nn":
            return "teikn igjen";
        case "en":
            return "characters remaining";
    }
}
function renderMaxLengthText(valueLength, maxLengthProps, language) {
    if (!maxLengthProps) {
        return null;
    }
    const { maxLength: maxLength$1, remainingLettersText, exceededLettersText } = maxLengthProps;
    const exceedsMaxLength = valueLength > maxLengthProps.maxLength;
    const exceededText = exceededLettersText ?? getDefaultExceededText(language);
    const remainingText = remainingLettersText ?? getDefaultRemainingText(language);
    return (jsx("p", { role: "status", className: `${maxLength} ${exceedsMaxLength ? "exceeded" : ""}`, children: `${Math.abs(maxLength$1 - valueLength)} ${exceedsMaxLength ? exceededText : remainingText}` }));
}
const TextArea = forwardRef(({ textareaClass = "", extraWrapperClass = "", labelText = null, labelClass, descriptionText = null, descriptionClass, errorText = null, errorClass = warnText, errorIcon = null, focus = false, valid = true, id, maxLengthProps, children, value, onChange, ...otherProps }, ref) => {
    const uniqueId = useId();
    const textareaId = id || `textarea-${uniqueId}`;
    const [valueLength, setValueLength] = useState(value?.length ?? 0);
    const { language } = useLanguage();
    return (jsxs("div", { className: `${textareaContainer} ${extraWrapperClass}`, children: [labelText !== null && (jsx("label", { className: labelClass, htmlFor: textareaId, children: labelText })), descriptionText !== null && (jsx("div", { className: descriptionClass, id: `${textareaId}-description`, children: descriptionText })), jsx("textarea", { ref: ref, "aria-describedby": `${textareaId}-description`, "aria-errormessage": errorText !== null ? `${textareaId}-error` : undefined, "aria-invalid": !valid, className: `${textareaClass} ${valid ? "" : warn}`, id: textareaId, autoFocus: focus, value: value, onChange: (event) => {
                    onChange?.(event);
                    if (!event.defaultPrevented) {
                        setValueLength(event.target.value.length);
                    }
                }, ...otherProps }), renderMaxLengthText(valueLength, maxLengthProps, language), errorText !== null && (jsxs("div", { className: errorClass, role: "status", id: `${textareaId}-error`, children: [errorIcon, errorText] })), children] }));
});
TextArea.displayName = "TextArea";

export { Input, PasswordInput, TextArea };
